import { Allotment } from 'allotment'
import { useEffect, useState } from 'react'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { runsSelectors } from '@/entities/runs'
import { fetchRuns } from '@/entities/runs/thunks/fetchRuns'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { Box, Divider, Grid, styled, Typography, useTheme } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane } from '../StyledOutletPage'
import { RunEditForm } from './RunEditForm'

export const RunsPage = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)

  const [formType, setFormType] = useState<'add' | 'edit' | 'view'>('add')
  const [editMode, setEditMode] = useState<'single' | 'multi'>('single')
  const [loading, setLoading] = useState(false)

  const dispatch = useAppDispatch()
  const theme = useTheme()
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)
  const runs = useAppSelector(state => runsSelectors.selectAll(state))
    .filter(run => run.groupId === activeGroupId)
  const { data: groupOptions } = useDropdown('groups')
  const { data: depotOptions } = useDropdown('depots')

  // Fetch drivers from server
  const fetchRunsFromServer = async () => {
    setLoading(true)
    await dispatch(fetchRuns())
    setLoading(false)
  }

  useEffect(() => {
    fetchRunsFromServer()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])


  // Define the columns and rows for the grid
  const rows = runs.map(item => ({
    ...item,
    isSelectable: true,
    startDepotId: item.startDepotId,
    finishDepotId: item.finishDepotId,
  }))

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'groupId',
      headerName: 'Group',
      width: 150,
      valueGetter: (value) => {
        return groupOptions.find(g => g.value === value)?.label || ''
      },
    },
    {
      field: 'startDepotId',
      headerName: 'Start Depot',
      width: 150,
      valueGetter: (value) => {
        if (!value) return ''

        const depot = depotOptions.find(d => d.value === value)

        return depot ? `${depot.depotCode} - ${depot.label}` : ''
      },
    },
    {
      field: 'finishDepotId',
      headerName: 'Finish Depot',
      width: 150,
      valueGetter: (value) => {
        if (!value) return ''

        const depot = depotOptions.find(d => d.value === value)

        return depot ? `${depot.depotCode} - ${depot.label}` : ''
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'uiColor',
      headerName: 'Color',
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: pxToRem(14),
              height: pxToRem(14),
              borderRadius: pxToRem(2),
              bgcolor: params.value,
              marginRight: theme.spacing(1),
            }}
          />
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      valueGetter: (value) => value ? 'Active' : 'Disabled',
    },
  ]

  const handleRowSelection = (model: GridInputRowSelectionModel) => {
    setRowSelectionModel(model)

    if (Array.isArray(model) && model.length > 0) {
      setShowDetails(true)
      setFormType('view')
      setEditMode(model.length > 1 ? 'multi' : 'single')
    } else {
      setShowDetails(false)
    }
  }

  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Runs</StyledHeader>
                <StyledDescription>
                  Configure run definitions, working hours, and costing parameters.
                </StyledDescription>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              loading={loading}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelection}
              initialState={{
                filter: {
                  filterModel: {
                    items: [{
                      field: 'isActive', operator: 'equals', value: 'Active',
                    }],
                  },
                },
              }}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              sx={{
                '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            {editMode === 'single' ? (
              <RunEditForm
                runId={Array.isArray(rowSelectionModel) ? rowSelectionModel[0] : rowSelectionModel as EntityId}
                mode="single"
                formType={formType}
                setFormType={setFormType}
                setRowSelectionModel={setRowSelectionModel}
                onClose={() => setShowDetails(false)}
              />
            ) : (
              <Typography variant="body1">
                Multi-edit not supported yet
              </Typography>
            )}
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  max-height: calc(100% - ${pxToRem(80)});
`